import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { User } from "../types/frontend";
import { fetchAuthenticatedUser } from "../api/auth";

/**
 * Type for authentication event types used in cross-tab communication
 */
enum AuthEventType {
  LOGIN = "login",
  LOGOUT = "logout",
}

/**
 * Interface defining the shape of the authentication context
 */
interface AuthContextType {
  /** Whether the user is currently authenticated */
  isAuthenticated: boolean;
  /** Whether the user's email is verified */
  isEmailVerified: boolean;
  /** Whether authentication state is currently being loaded/checked */
  isLoading: boolean;
  /** The currently authenticated user object, or null if not authenticated */
  user: User | null;
  /** Function to log in a user and update the authentication state */
  loginUser: (user: User) => Promise<void>;
  /** Function to log out the current user and clear authentication state */
  logoutUser: () => void;
  /** Function to set the email verification status */
  setIsEmailVerified: (isEmailVerified: boolean) => void;
}

/**
 * Create the authentication context with undefined as default value
 */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Props interface for the AuthProvider component
 */
interface AuthProviderProps {
  /** Child components that will have access to the authentication context */
  children: ReactNode;
}

/**
 * Create a broadcast channel for cross-tab authentication synchronization
 * This allows authentication state changes in one tab to be reflected in other tabs
 * Falls back to null if BroadcastChannel is not supported (e.g., in SSR environments)
 */
const authChannel =
  typeof window !== "undefined" && "BroadcastChannel" in window
    ? new BroadcastChannel("auth")
    : null;

/**
 * AuthProvider Component
 *
 * Provides authentication context to the entire application.
 * Manages JWT tokens, user state, and authentication status.
 * Handles cross-tab synchronization of authentication state.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Authentication state variables
  /** Whether the user is currently authenticated */
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  /** Whether the user's email is verified */
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  /** Whether authentication state is currently being loaded/checked */
  const [isLoading, setIsLoading] = useState(true);
  /** The currently authenticated user object, or null if not authenticated */
  const [user, setUser] = useState<User | null>(null);

  /**
   * Set authenticated state function
   * Sets the authentication state to true and the authentication token and user
   *
   * @param authenticated - The authentication state to set
   * @param user - The user to set
   */
  const setAuthenticatedState = (
    authenticated: boolean,
    user: User | null
  ): void => {
    setIsAuthenticated(authenticated);
    setUser(user);
  };

  /**
   * Initialize authentication state
   * Fetches the authenticated user from the API and sets the authentication state
   * This function is called on component mount and when auth state changes in other tabs
   */
  const initializeAuth = async () => {
    // Fetch the authenticated user from the API using HttpOnly cookies
    const user = await fetchAuthenticatedUser();

    if (user) {
      // User is authenticated, set the state accordingly
      setAuthenticatedState(true, user);
      setIsEmailVerified(!!user.email_verified_at);
    } else {
      // User is not authenticated, clear the state
      setAuthenticatedState(false, null);
    }
    // Authentication check is complete, stop loading
    setIsLoading(false);
  };

  /**
   * Initialize authentication state on component mount
   * This effect runs once when the component mounts to check if the user is already authenticated
   */
  useEffect(() => {
    initializeAuth();
  }, []);

  /**
   * Listen for cross-tab authentication messages
   * This effect sets up a listener for BroadcastChannel messages to synchronize
   * authentication state across multiple browser tabs
   */
  useEffect(() => {
    // Exit early if BroadcastChannel is not available
    if (!authChannel) return;

    /**
     * Handle incoming messages from other tabs
     * @param e - The message event containing authentication state changes
     */
    const onMessage = (e: MessageEvent) => {
      const { type } = e.data || {};
      if (type === AuthEventType.LOGOUT) {
        // Another tab logged out, update local state
        setAuthenticatedState(false, null);
        setIsLoading(false);
      } else if (type === AuthEventType.LOGIN) {
        // Another tab logged in, re-validate user state
        initializeAuth();
      }
    };

    // Add event listener for cross-tab messages
    authChannel.addEventListener("message", onMessage);

    // Cleanup function to remove event listener
    return () => authChannel.removeEventListener("message", onMessage);
  }, [initializeAuth]);

  /**
   * Broadcast authentication state changes to other tabs
   * Uses BroadcastChannel if available, falls back to localStorage events
   *
   * @param type - The type of authentication event ("login" or "logout")
   */
  const broadcast = (type: AuthEventType) => {
    if (authChannel) {
      // Use BroadcastChannel for modern browsers
      authChannel.postMessage({ type, ts: Date.now() });
    } else {
      // Fallback to localStorage for older browsers or environments without BroadcastChannel
      localStorage.setItem(
        "auth-event",
        JSON.stringify({ type, ts: Date.now() })
      );
    }
  };

  /**
   * Login user function
   * Sets the user as authenticated in the context and broadcasts the login event to other tabs
   *
   * @param user - The user object to set as the authenticated user
   * @throws Error if setting authentication state fails
   */
  const loginUser = async (user: User): Promise<void> => {
    // Attempt to login user and set authentication state
    try {
      // Set authentication state and persist user information to cookies
      setAuthenticatedState(true, user);
      setIsEmailVerified(!!user.email_verified_at);

      // Notify other tabs about the authentication change
      broadcast(AuthEventType.LOGIN);
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  };

  /**
   * Logout user function
   * Clears the authentication token and user in the context
   * Removes JWT token from cookies and notifies other tabs
   */
  const logoutUser = (): void => {
    // Reset authentication state
    setAuthenticatedState(false, null);

    // Notify other tabs about the logout
    broadcast(AuthEventType.LOGOUT);
  };

  // Create the context value object with all authentication state and functions
  const contextValue: AuthContextType = {
    isAuthenticated,
    isLoading,
    user,
    loginUser,
    logoutUser,
    isEmailVerified,
    setIsEmailVerified,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

/**
 * Custom hook to use authentication context
 * Provides access to authentication state and functions
 *
 * @returns The authentication context value
 * @throws Error if used outside of AuthProvider
 */
export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
